// @flow

import * as React from 'react';

import clsx from 'clsx';
import { useDispatch } from 'react-redux';

import { notifyDanger } from '@gandi/notifications';
import { sendInternalSearch } from '@gandi/tracking';
import { useTranslator } from '@gandi/translate';
import { Base, type DialogStateProps } from '@gandi/ui';

import AlphabetSelect from 'src/Domain/AlphabetSelect';
import { FqdnCell } from 'src/Domain/FqdnCell';
import { Icon } from 'src/Domain/Icon';
import { ProvidedPlaceholderRow } from 'src/Domain/Placeholder/ProvidedPlaceholderRow';
import { ProvidedRowContainer } from 'src/Domain/ProvidedRowContainer';
import styles from 'src/Domain/ProvidedRowContainer.scss';
import type { GandiFormSelectOption } from 'src/Domain/types';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { addToCart, removeFromCart } from '../../../modules/actions/cart';
import { fetchIDNLookup } from '../../../modules/actions/idn';
import { select as selectIDNLanguages } from '../../../modules/selectors/idn';
import { selectPagination } from '../../../modules/selectors/pagination';
import { suggestionThunkCallback } from '../../../modules/selectors/results';
import { selectSearchValue } from '../../../modules/selectors/search';
import type { ExtendedSuggestion, IDNLangAlpha2Code } from '../../../types';

import { RestrictionBlock } from '../../DomainList/RestrictionBlock';
import { setModalState } from '../../PhasesDialog/module';
import { ProvidedDomainPrice } from '../PriceArea/ProvidedDomainPrice';
import { AvailabilityText } from '../ProvidedDomainList/AvailabilityText';
import { useExtendedSuggestion } from '../modules';

const ITEM_CLASSNAME = 'qa-domain-row';

type Props = {
  className?: string,
  fqdn: string,
  phasesDialogState: DialogStateProps,
  isAddingToCart: boolean,
  setIsAddingToCart: boolean => void,
};
export function ProvidedRow({
  className,
  fqdn,
  phasesDialogState,
  setIsAddingToCart,
  isAddingToCart,
}: Props): React.MixedElement {
  const { suggestion, isEarlyAccess, needsPhaseSelection, phases, valid } =
    useExtendedSuggestion({
      fqdn,
    });
  const dispatch = useDispatch();
  const { __ } = useTranslator();

  const [lang, setLang] = React.useState<IDNLangAlpha2Code>('');
  const [isLoadingLang, setLoadingLang] = React.useState(false);
  const [error, setError] = React.useState<?boolean>(false);

  const searchValue = useAppSelector(selectSearchValue);
  const currentPagination = useAppSelector(selectPagination);
  const IDNAvailableLanguages = useAppSelector(selectIDNLanguages);

  // fetchIDNLookup hook
  const availability = suggestion?.availability; // important to extract the only props we use to avoid infinity loop

  React.useEffect(() => {
    if (!fqdn || !availability || !lang) return;

    setLoadingLang(true);
    setError(undefined);

    dispatch(fetchIDNLookup(fqdn, lang, availability === 'lang_required')).then(
      () => {
        setLoadingLang(false);
      },
      fetchError => {
        let message;

        switch (fetchError._error) {
          case 'availability':
            message = __('This domain name is not available in this language.');
            break;
          case 'fetch':
          case 'prices':
          default:
            message = __(
              'Unable to fetch prices and/or availability for this domain name in this language.',
            );
            break;
        }

        dispatch(notifyDanger(message));

        setError(fetchError);
        setLoadingLang(false);
      },
    );
  }, [__, dispatch, fqdn, lang, availability]);

  if (!suggestion) return <ProvidedPlaceholderRow fqdn={fqdn} />;

  const isReady = !!(
    suggestion.availability &&
    !isLoadingLang &&
    (suggestion.prices ||
      suggestion.error ||
      suggestion.availability === 'lang_required')
  );

  const displayLangSelect =
    isReady &&
    suggestion.availability === 'lang_required' &&
    Array.isArray(IDNAvailableLanguages) &&
    IDNAvailableLanguages.length > 0;

  const isErrorWithRestriction = suggestion.error && !!suggestion.restriction;

  const handleLangChange: (
    option: GandiFormSelectOption<IDNLangAlpha2Code>,
  ) => void = (option: GandiFormSelectOption<IDNLangAlpha2Code>) => {
    const newLang: IDNLangAlpha2Code = option.value;

    if (newLang !== lang) setLang(newLang);
  };

  const updateCartProducts = (lastSuggestion: ExtendedSuggestion) => {
    // Get UI position of the target in the list from the DOM
    const UIPosition =
      Array.from(document.querySelectorAll(`.${ITEM_CLASSNAME}`)).indexOf(
        document.getElementById(fqdn),
      ) + 1;
    const options = {
      optimistic: lastSuggestion.optimistic,
      UIPosition,
      lang,
    };

    sendInternalSearch({
      type: 'click',
      keyword: searchValue,
      click_rank: UIPosition,
      // because of line 655, currentPagination is undefined if it's a bundled suggestion
      page: currentPagination?.page || 1,
    });

    setIsAddingToCart(true);
    setLoadingLang(true);

    if (lastSuggestion.addedToCart) {
      dispatch(removeFromCart(lastSuggestion, options))
        .catch(() =>
          // $FlowFixMe[incompatible-call]
          dispatch(
            notifyDanger(__('Unable to remove the domain create in the cart.')),
          ),
        )
        .finally(() => {
          setIsAddingToCart(false);
          setLoadingLang(false);
        });
    } else {
      dispatch(addToCart(lastSuggestion, options))
        .catch(() => {
          dispatch(
            notifyDanger(__('Unable to add the domain create in the cart.')),
          );
        })
        .finally(() => {
          setIsAddingToCart(false);
          setLoadingLang(false);
        });
    }
  };

  const phaseSelectionCallback = suggestionThunkCallback(
    fqdn,
    updateCartProducts,
    __,
  );

  const onClickItem: (evt: SyntheticMouseEvent<>) => Promise<void> = async (
    evt: SyntheticMouseEvent<>,
  ) => {
    evt.preventDefault();

    if (needsPhaseSelection) {
      if (!suggestion.addedToCart) {
        dispatch(
          // $FlowFixMe[not-an-array]
          setModalState({
            phases,
            tld: suggestion.tld,
            fqdn: suggestion.fqdn,
            premium: suggestion.premium,
            prices: suggestion.prices,
            currency: suggestion.currency,
            callback: phaseSelectionCallback,
          }),
        );
        phasesDialogState.show();
        return;
      }
    }

    dispatch(phaseSelectionCallback);
  };

  const available = isReady && valid;

  const isCartActionnable =
    !suggestion.optimistic &&
    !isAddingToCart &&
    (available || suggestion.addedToCart) &&
    !isLoadingLang &&
    !error;

  const isValid = !!(
    !suggestion.error &&
    availability === 'available' &&
    suggestion.prices &&
    suggestion.prices.create
  );

  const displayRestrictionPanel = !!(
    isValid &&
    suggestion.restriction &&
    suggestion.forceDisplayRestriction
  );

  return (
    <>
      <ProvidedRowContainer
        id={fqdn}
        className={clsx(ITEM_CLASSNAME, className)}
        availabilityIconName={suggestion.availability}
        availabilityText={
          <AvailabilityText
            addedToCart={suggestion.addedToCart}
            availability={suggestion.availability}
            isEarlyAccess={isEarlyAccess}
          />
        }
        forceDisplayRestriction={displayRestrictionPanel}
        fqdn={suggestion.fqdn}
        valid={valid}
        premium={suggestion.premium}
        corporate={suggestion.corporate}
        isErrorWithRestriction={isErrorWithRestriction}
        cheapRenew={suggestion.prices?.renew?.cheap}
        beforePriceArea={
          displayLangSelect && (
            <AlphabetSelect
              id={`alphabet-select-${fqdn}`}
              value={lang}
              onChange={handleLangChange}
              big
            />
          )
        }
        onClickItem={onClickItem}
        isCartActionnable={!!isCartActionnable}
        addedToCart={!!suggestion.addedToCart}
        loading={isLoadingLang}
      >
        <ProvidedDomainPrice fqdn={fqdn} />
      </ProvidedRowContainer>
      {suggestion.restriction && displayRestrictionPanel && (
        <RestrictionBlock fqdn={fqdn} restriction={suggestion.restriction} />
      )}
    </>
  );
}

ProvidedRow.Error = function ProvidedRowError({
  fqdn,
}: {
  fqdn: string,
}): React.MixedElement {
  return (
    <div className={styles.root}>
      <div>
        <Icon iconName="error" />
        <AvailabilityText availability="error" isEarlyAccess={false} />
        <Base
          flex
          size="bigger"
          weight="semibold"
          flexAlignItemsCenter
          flexWrap
          className={styles.domainProductDescription}
        >
          <FqdnCell.Error fqdn={fqdn} />
        </Base>
      </div>
    </div>
  );
};
